<script setup lang="ts">
import type { Feature } from '@/shared/config/permissions'
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { usePermissions } from '@/shared/composables/usePermissions'
import CButton from './CButton.vue'

interface Props {
  feature: Feature
  showBadge?: boolean
  compact?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showBadge: false,
  compact: false,
})

const router = useRouter()
const { hasAccess } = usePermissions()

const hasFeatureAccess = computed(() => hasAccess(props.feature))

const premiumFeatures = [
  'tariff_feature_repricer_full',
  'tariff_feature_autobidder',
  'tariff_feature_multi_store',
  'tariff_feature_priority_support',
]

function handleUpgrade() {
  router.push('/tariffs')
}
</script>

<template>
  <div class="relative h-full">
    <!-- Content when user has access -->
    <template v-if="hasFeatureAccess">
      <slot />
    </template>

    <!-- Restricted view when user doesn't have access -->
    <template v-else>
      <slot name="restricted">
        <!-- Default restricted card -->
        <div
          class="flex items-center justify-center p-6 h-full"
          :class="[compact ? 'min-h-[150px]' : 'min-h-[300px]']"
        >
          <div class="max-w-md w-full">
            <!-- Premium Card -->
            <div
              class="bg-white rounded-2xl shadow-lg border border-border-lighter overflow-hidden"
            >
              <!-- Header with gradient -->
              <div
                class="bg-gradient-to-r from-brand-violet to-purple-500 px-6 py-5 text-white"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center"
                  >
                    <i class="icon-crown-minimalistic-outline text-3xl" />
                  </div>
                  <div>
                    <h3 class="text-lg font-bold">Premium</h3>
                    <p class="text-white/80 text-sm">
                      {{ $t("upgrade_to_premium") }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Content -->
              <div class="p-6">
                <p class="text-text-secondary text-sm mb-4">
                  {{ $t("feature_requires_premium") }}
                </p>

                <!-- Features list -->
                <div class="space-y-2 mb-6">
                  <div
                    v-for="feature in premiumFeatures"
                    :key="feature"
                    class="flex items-center gap-2 text-sm text-text-primary"
                  >
                    <i class="icon-tick text-green-500 text-lg flex-shrink-0" />
                    <span>{{ $t(feature) }}</span>
                  </div>
                </div>

                <!-- CTA Button -->
                <CButton
                  type="primary"
                  class="w-full h-11"
                  @click="handleUpgrade"
                >
                  <i class="icon-cash text-xl mr-2" />
                  {{ $t("view_plans") }}
                </CButton>
              </div>
            </div>
          </div>
        </div>
      </slot>
    </template>

    <!-- Premium badge (optional) -->
    <div
      v-if="showBadge && !hasFeatureAccess"
      class="absolute -top-2 -right-2 z-10"
    >
      <span
        class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-violet/10 text-brand-violet text-xs font-medium"
      >
        <i class="icon-crown-minimalistic-outline text-xs" />
        Premium
      </span>
    </div>
  </div>
</template>
